import type { ComboboxItem } from '@mantine/core';
import i18n from '../../../../../base-dictionary';
import type { FilterOption } from '../../../../../components/filters/filter-panel.type';
import { capitalizeFirstLetter } from '../../../../../helpers/string-helpers/string-helpers';
import { secondaryBase } from '../../../../../styles/design-tokens';
import { Icon } from '../../../../../ui-library/icon/icon';
import type { ExtendedComboboxItem } from '../../../../../ui-library/select/select.type';
import { Phase } from '../../flow-by-phase-tab/flow-by-phase-tab.type';
import { RelatedTasksMode } from '../related-tasks-tab.type';

/**
 * Returns an array of filter options for task types
 * Each option contains the task type as value and a capitalized label
 *
 * @param {string[]} taskTypes - Array of task type strings to convert to filter options
 * @returns {FilterOption[]} Array of filter options with task type values and capitalized labels
 */
const getTaskTypeOptions = (taskTypes: string[]): FilterOption[] => {
  return taskTypes.map((type) => ({
    value: type,
    label: capitalizeFirstLetter(type),
  }));
};

/**
 * Returns an array of all available phase options
 *
 * @returns {ComboboxItem[]} Array of all phase options
 */
const getPhaseOptions = (): ComboboxItem[] => {
  return [
    {
      value: 'all',
      label: i18n.t('strategy.filters.all_statuses'),
    },
    {
      value: Phase.Blocked,
      label: i18n.t('strategy.initiative_performance.phases.blocked'),
    },
    {
      value: Phase.Backlog,
      label: i18n.t('strategy.initiative_performance.phases.backlog'),
    },
    {
      value: Phase.Wip,
      label: i18n.t('strategy.initiative_performance.phases.wip'),
    },
    {
      value: Phase.Review,
      label: i18n.t('strategy.initiative_performance.phases.in_review'),
    },
    {
      value: Phase.Test,
      label: i18n.t('strategy.initiative_performance.phases.in_test'),
    },
    {
      value: Phase.Deployable,
      label: i18n.t('strategy.initiative_performance.phases.to_deploy'),
    },
    {
      value: Phase.Done,
      label: i18n.t('strategy.initiative_performance.phases.done'),
    },
  ];
};

/**
 * Returns an array of options for the related tasks mode dropdown
 * Each option contains a label for display and a value from the RelatedTasksMode enum
 *
 * @returns {ExtendedComboboxItem[]} Array of options for the related tasks mode selection
 */
const getRelatedTasksModeOptions = (): ExtendedComboboxItem[] => {
  return [
    {
      label: i18n.t('strategy.initiative_performance.labels.contributing_tasks'),
      value: RelatedTasksMode.ContributingTasks,
      leftSection: <Icon name="task_alt" size={20} color={secondaryBase} />,
    },
    {
      label: i18n.t('strategy.initiative_performance.labels.contributing_epics'),
      value: RelatedTasksMode.ContributingEpics,
      leftSection: <Icon name="move_down" size={20} color={secondaryBase} />,
    },
    {
      label: i18n.t('strategy.initiative_performance.labels.contributing_teams'),
      value: RelatedTasksMode.ContributingTeams,
      leftSection: <Icon name="people_alt" size={20} color={secondaryBase} />,
    },
  ];
};

export { getPhaseOptions, getRelatedTasksModeOptions, getTaskTypeOptions };
